import constants from './constants.js';

/**
 * Handle posting file data to S3
 */
export class XhrHandler {
	/**
	 * Create the handler with the given options
	 * @param {Object} options
	 * @property {plupload.File} file  The plupload.File object to send
	 * @property {Function<String>} onerror  The callback to fire with any error messages
	 * @property {Function} onprogress  The callback to trigger when upload progress is made
	 * @property {Function} oncomplete  The callback to trigger when upload is complete
	 */
	constructor(options) {
		this.options = options;
		const pluploadFile = this.options.file;
		this.xhr = new XMLHttpRequest();
		// for information on XMLHttpRequest v2 events
		// see https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/Using_XMLHttpRequest
		this.xhr.onerror = () => this.options.onerror('Network error.');
		this.xhr.ontimeout = () => this.options.onerror('Server timeout.');
		this.xhr.upload.addEventListener(
			'progress',
			evt => {
				pluploadFile.loaded = evt.loaded;
				pluploadFile.total = evt.total;
				pluploadFile.percent = evt.total ? Math.ceil((evt.loaded / evt.total) * 100) : 0;
				this.options.onprogress();
			},
			false
		);
		this.xhr.onreadystatechange = () => {
			if (this.xhr.readyState === XMLHttpRequest.DONE) {
				pluploadFile.status = constants.DONE;
				pluploadFile.completeTimestamp = +new Date();
				if (this.xhr.status < 200 || this.xhr.status >= 300) {
					pluploadFile.status = constants.FAILED;
					const message = pluploadFile.wasAborted ? 'Upload aborted.' : 'Server error on upload.';
					this.options.onerror(message);
				}
				this.options.oncomplete();
			}
		};
		this.xhr.open('POST', this.options.url, true);
	}

	/**
	 * Get the POST payload for the given file
	 * @returns {FormData}
	 * @private
	 */
	_getPayload() {
		const pluploadFile = this.options.file;
		const formData = new FormData();
		const filename = this._lowerExtension(pluploadFile.name);
		Object.keys(this.options.multipart_params).forEach(key => {
			let value = this.options.multipart_params[key];
			if (typeof value == 'string') {
				// eslint-disable-next-line no-template-curly-in-string
				value = value.replace('${filename}', filename);
				formData.append(key, value);
			}
		});
		formData.append('name', filename);
		formData.append('file', pluploadFile.getNative(), filename);
		return formData;
	}

	/**
	 * Return a filename with the extension lowercased
	 * @param {String} filename
	 * @returns {String}
	 * @private
	 */
	_lowerExtension(filename) {
		return filename.replace(/\.[^.]+$/, $0 => $0.toLowerCase());
	}

	/**
	 * Kick off the XHR
	 */
	send() {
		this.xhr.send(this._getPayload());
		const pluploadFile = this.options.file;
		pluploadFile.isStarted = true;
		pluploadFile.cancel = () => {
			if (pluploadFile.status !== constants.DONE) {
				pluploadFile.wasAborted = true;
				this.xhr.abort(); // onreadystatechange will fire next
			}
		};
	}
}
